import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { StatisticCardComponent } from 'app/shared/components/statistic-card/statistic-card.component';
import { TitleHeaderComponent } from 'app/shared/components/title-header/title-header.component';
import { PROBLEM_RECORDS_DASHBOARD_TABLE_STATE, PROBLEM_RECORDS_TABLE_CONFIG } from './problem-records-table.config';
import { finalize, Subject, take, takeUntil } from 'rxjs';
import { ProblemRecordsService } from './problem-records.service';
import { DashboardProblemRecordCardsEnum } from 'app/shared/enums/problem-records.enum';
import { DataTableColumnFilters, DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { ProblemRecordsList } from 'app/shared/models/problem-records.model';
import { ListResponse } from 'app/shared/models/list-response.model';
import { Cards, CardsResponse } from 'app/shared/models/cards.model';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { HelperService } from 'app/shared/services/helper.service';
import { GlobalSearchComponent } from 'app/shared/components/global-search/global-search.component';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-problem-records',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    RouterLink,
    TitleHeaderComponent,
    DynamicTableComponent,
    StatisticCardComponent,
    GlobalSearchComponent
  ],
  templateUrl: './problem-records.component.html',
  styleUrl: './problem-records.component.scss'
})
export class ProblemRecordsComponent {
  private readonly _destroy$ = new Subject<void>();
  cardsList: Cards[] = [];
  problemRecords: ProblemRecordsList[] = [];
  totalCount: number = 0;
  columns = this.helperService.deepCopy(PROBLEM_RECORDS_TABLE_CONFIG);
  datatableDisplayAllNames: DataTableColumnFilters[] = [];
  isDataTableReady: boolean = false;
  filters: DataTableColumnFiltersApi[] = [];
  problemRecordsDatatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(PROBLEM_RECORDS_DASHBOARD_TABLE_STATE.gridName);
  problemRecordsDataTableState: DataTableStateModel = this.problemRecordsDatatableStateFromStorage ? this.problemRecordsDatatableStateFromStorage : { ...PROBLEM_RECORDS_DASHBOARD_TABLE_STATE };
  defaultColumns = this.helperService.deepCopy(PROBLEM_RECORDS_TABLE_CONFIG);
  selectedFilterName = '';

  constructor(
    private problemRecordsService: ProblemRecordsService,
    private router: Router,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private activeRoute: ActivatedRoute,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.activeRoute.queryParams
      .pipe(takeUntil(this._destroy$))
      .subscribe((params) => {
        const card: string = params['card'];
        if (card && Object.keys(DashboardProblemRecordCardsEnum).some((key: string) => (DashboardProblemRecordCardsEnum as any)[key] === card)) {
          this.problemRecordsDataTableState.tableType = card;
        }
        this.setSelectedCardToUrlQueryParams(this.problemRecordsDataTableState.tableType);
      });
    this.getDataTableConfiguration();
  }

  setSelectedCardToUrlQueryParams(selectedCard: string) {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { card: selectedCard },
      queryParamsHandling: 'merge'
    });
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.problemRecordsDataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.setData();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.problemRecordsDatatableStateFromStorage) {
          this.columns = this.problemRecordsDatatableStateFromStorage.datatableColumns;
          this.filters = this.problemRecordsDatatableStateFromStorage.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.problemRecordsDataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.problemRecordsDataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }

        if (res && res.name) {
          this.selectedFilterName = res.name;
        }

        this.getStatisticProblemRecordsCards();
      })
  }

  setData() {
    this.setDataTableColumnFilters();
    this.datatableDisplayAllNames = [
      ...this.configurationService.getProblemRecordPriorityItems(),
      ...this.configurationService.getProblemRecordStatusItems(),
      ...this.configurationService.getProblemRecordStageItems(),
      ...this.configurationService.getNextActionStatusItemsForFilter(),
      ...this.configurationService.getBooleanDataTableColumnFilters(),
    ];

    this.isDataTableReady = true;
    this.getProblemRecords();
  }

  setDataTableColumnFilters() {
    this.columns.forEach((column: any) => {
      switch (column.prop) {
        case 'problemPriority':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordPriorityItems();
          break;
        case 'problemStatus':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStatusItems();
          break;
        case 'problemStage':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStageItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
      }
    });

    this.defaultColumns.forEach((column: any) => {
      switch (column.prop) {
        case 'problemPriority':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordPriorityItems();
          break;
        case 'problemStatus':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStatusItems();
          break;
        case 'problemStage':
          column.dataTableColumnFilters = this.configurationService.getProblemRecordStageItems();
          break;
        case 'nextActionStatus':
          column.dataTableColumnFilters = this.configurationService.getNextActionStatusItemsForFilter();
          break;
      }
    });
  }

  getStatisticProblemRecordsCards(): void {
    this.problemRecords = [];

    this.problemRecordsService
      .getStatisticProblemRecordsCards(this.problemRecordsDataTableState)
      .pipe(take(1))
      .subscribe((res: CardsResponse[]) => {
        this.setStatisticProblemRecordsData(res);
      });
  }

  setStatisticProblemRecordsData(data: CardsResponse[]) {
    this.cardsList = [];
    data.forEach((element: CardsResponse) => {
      switch (element.type) {
        case DashboardProblemRecordCardsEnum.ALL:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.all_problem_records',
            iconClass: 'bi bi-infinity',
            color: '#A6AAAB',
          });
          break;
        case DashboardProblemRecordCardsEnum.COMPLEX:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.complex',
            iconClass: 'bi bi-emoji-dizzy',
            color: '#842029',
          });
          break;
        case DashboardProblemRecordCardsEnum.COMPLICATED:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.complicated',
            iconClass: 'bi bi-emoji-neutral',
            color: '#B00010',
          });
          break;
        case DashboardProblemRecordCardsEnum.SIMPLE:
          this.cardsList.push({
            value: element.count,
            type: element.type,
            description: 'label.simple',
            iconClass: 'bi bi-emoji-smile',
            color: '#E6BA00',
          });
          break;
      }
    });
  }

  getProblemRecords() {
    this.problemRecords = [];
    this.problemRecordsService.getElkProblemRecordsDashboardList(this.problemRecordsDataTableState)
      .pipe(take(1))
      .subscribe((res: ListResponse<ProblemRecordsList[]>) => {

        this.problemRecords = res?.items.map((c: any) => ({
          ...c,
          externalCellClass: this.helperService.getColorOfNextActionDueDate(c.nextActionDueDate)
        })) || [];

        this.totalCount = res?.totalCount || 0;
      });
  }

  getActiveType(data: string) {
    this.problemRecordsDataTableState.tableType = data;
    this.problemRecordsDataTableState.pageNumber = 1;
    this.problemRecordsDataTableState.datatableColumns = this.columns;
    this.setSelectedCardToUrlQueryParams(this.problemRecordsDataTableState.tableType);
    this.getProblemRecords();
    this.getStatisticProblemRecordsCards();
    this.helperService.storeGridFilterObjectToSessionStorage(this.problemRecordsDataTableState.gridName, this.problemRecordsDataTableState);
  }

  getDataTableState(data: DataTableStateModel) {
    this.problemRecordsDataTableState = data;
    this.getProblemRecords();
    this.getStatisticProblemRecordsCards();
  }

  getLinkedValue(data: ProblemRecordsList | any) {
    this.router.navigate(['problem-record/' + data.row.id]);
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);
    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.problemRecordsDataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    }

    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.problemRecordsDataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }

}
